import { useCallback, useEffect, useState } from "react";

import { Button, Spinner } from "@blueprintjs/core";
import styled from "@emotion/styled";
import uniqBy from "lodash/uniqBy";

import { pluralize } from "@/components/helpers/string-utils";
import { Box, Flex } from "@/components/layout/flexbox";
import { breakpoints } from "@/components/layout/Media";
import { RoleplayListHeader } from "@/components/pages_logged_in/roleplays/RoleplayListHeader";
import { useScenarioQueryParams } from "@/components/pages_logged_in/roleplays/scenarios/hooks";
import { sortScenariosByDifficulty } from "@/components/pages_logged_in/roleplays/utils";
import { Paging } from "@/components/pages_logged_in/workspace-admin/Paging";
import { Container } from "@/components/pieces/bootstrap";
import { PermissionLevel, useScenariosListQueryLazyQuery } from "@/graphql";
import { ConfigureInput, NextInputProps } from "@/types/programs";
import { ProgramScenario } from "@/types/roleplays";
import { ensureNonNullArray } from "@/utils/array";

import { ProgramScenarioCard } from "./ProgramScenarioCard";

interface ChooseScenariosProps {
  onNext: (data: NextInputProps) => void;
  input: ConfigureInput;
}

export function ChooseScenarios({ onNext, input }: ChooseScenariosProps) {
  const [queryAllScenarios, { data: scenariosData, loading }] =
    useScenariosListQueryLazyQuery({
      fetchPolicy: "no-cache",
    });

  const [scenarios, setScenarios] = useState<ProgramScenario[]>([]);
  const [selectedScenarios, setSelectedScenarios] = useState<ProgramScenario[]>(
    [],
  );
  const [isViewAllCollections, setIsViewAllCollections] = useState(true);

  const {
    currentPage,
    setCurrentPage,
    selectedScopes,
    setSelectedScopes,
    selectedCollections,
    setSelectedCollections,
    selectedOwner,
    setSelectedOwner,
    selectedOrder,
    setSelectedOrder,
    selectedTags,
    setSelectedTags,
    query,
    setQuery,
  } = useScenarioQueryParams({});

  const pageSize = 10;

  useEffect(() => {
    queryAllScenarios({
      variables: {
        page: currentPage,
        pageSize,
        scopes: selectedScopes ? selectedScopes : [],
        query,
        includeOrphaned: isViewAllCollections
          ? null
          : selectedCollections.includes("-1"),
        permissionLevel: PermissionLevel.Share,
        orderBy: selectedOrder,
        tagIds: selectedTags,
        collectionIds: isViewAllCollections
          ? null
          : selectedCollections.filter((id) => id !== "-1"),
        isOwner: selectedOwner ? true : undefined,
      },
    });
  }, [
    currentPage,
    selectedScopes,
    query,
    selectedCollections,
    selectedOwner,
    selectedOrder,
    selectedTags,
    isViewAllCollections,
  ]);

  useEffect(() => {
    if (input.roleplayScenarios?.length) {
      const scenarios = ensureNonNullArray(input.roleplayScenarios);
      setSelectedScenarios(scenarios);
    }
  }, [input.roleplayScenarios]);

  useEffect(() => {
    const scenarios = ensureNonNullArray(scenariosData?.scenarios?.data);

    // Everything calling out of this component assumes that the scenarios are ProgramRoleplayScenarioType
    // conflicting type names cause TypeScript to throw errors so we need to cast to the correct type
    const filteredScenarios: ProgramScenario[] = scenarios
      .filter(
        (scenario) => !selectedScenarios?.find((s) => s?.id === scenario.id),
      )
      .map((scenario) => ({
        ...scenario,
        __typename: "ProgramRoleplayScenarioType",
      }));

    setScenarios(filteredScenarios);
  }, [scenariosData, selectedScenarios]);

  const onChoose = useCallback(() => {
    onNext({
      value: {
        selectedScenarioIds: selectedScenarios?.map((scenario) => scenario?.id),
        roleplayScenarios: selectedScenarios as ProgramScenario[],
      },
      isSave: true,
    });
  }, [onNext, selectedScenarios]);

  const renderListScenarios = (data: ProgramScenario[]) => {
    return data.map((scenario, index) => (
      <Box mt={index > 0 ? 24 : 0} key={scenario.id}>
        <ProgramScenarioCard
          scenario={scenario}
          onChoose={(selected) => {
            if (selected) {
              setSelectedScenarios(
                uniqBy([scenario, ...selectedScenarios], "id"),
              );
            } else {
              setSelectedScenarios(
                selectedScenarios.filter((s) => s.id !== scenario.id),
              );
            }
          }}
          isChosen={
            selectedScenarios.find((s) => s.id === scenario.id) !== undefined
          }
        />
      </Box>
    ));
  };

  const sortedScenarios = sortScenariosByDifficulty(scenarios);
  const totalRecords = scenariosData?.scenarios?.totalRecords || 0;

  return (
    <Box>
      <Container maxWidth={900}>
        <Box fontSize={24} fontWeight={600} mt={36}>
          Choose scenarios
        </Box>
        <Box color={"#778397"} fontSize={16} fontWeight={400}>
          Which scenarios do participants need to complete?
        </Box>

        <Box mt={16} mb={5}>
          {selectedScenarios.length > 0 && (
            <Box mb={24}>
              <Box fontSize={16} fontWeight={600} mb={16}>
                Selected scenarios
              </Box>
              {renderListScenarios(selectedScenarios)}
            </Box>
          )}
          <Box>
            <Box fontSize={16} fontWeight={600} mb={16}>
              Available scenarios
            </Box>
            <RoleplayListHeader
              showStatusFilter={false}
              filterFor="scenario"
              selectedScopes={selectedScopes}
              selectedOwner={selectedOwner}
              selectedCollections={selectedCollections}
              selectedTags={selectedTags}
              selectedScenarioOrder={selectedOrder}
              onChangeScope={(scopes) => {
                setSelectedScopes(scopes);
                setCurrentPage(1);
              }}
              onSearch={(query) => {
                setQuery(query ? query.trim() : null);
                setCurrentPage(1);
              }}
              onOwnerChange={(owner) => {
                setSelectedOwner(owner);
                setCurrentPage(1);
              }}
              loading={loading}
              allowAdd={false}
              onScenarioOrderChange={(order) => {
                setSelectedOrder(order);
                setCurrentPage(1);
              }}
              onTagsChange={(tagIds) => {
                setSelectedTags(tagIds);
                setCurrentPage(1);
              }}
              onCollectionsChange={(collectionIds) => {
                setSelectedCollections(collectionIds);
                setCurrentPage(1);
              }}
              onViewAllCollections={(isViewAll) => {
                setIsViewAllCollections(isViewAll);
              }}
            />
            {loading ? (
              <Flex justifyContent="center" py={24}>
                <Spinner size={30} />
              </Flex>
            ) : (
              renderListScenarios(sortedScenarios)
            )}
            <Box mt={24}>
              <Paging
                totalRecords={totalRecords}
                pageSize={pageSize}
                currentPage={currentPage}
                setCurrentPage={setCurrentPage}
              />
            </Box>
          </Box>
        </Box>

        <Footer justifyContent="space-between" alignItems="center">
          <Box>
            {pluralize({
              amount: selectedScenarios?.length || 0,
              singular: "scenario",
              plural: "scenarios",
            })}
            {` included`}
          </Box>
          <Button
            intent="primary"
            large={true}
            onClick={onChoose}
            disabled={selectedScenarios?.length === 0}
          >
            Continue
          </Button>
        </Footer>
      </Container>
    </Box>
  );
}

const Footer = styled(Flex)`
  border-top: 1px solid #e5e5e5;
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background: white;
  padding: 10px 36px;

  @media (min-width: ${breakpoints.sm}px) {
    left: 210px;
  }
`;
